import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import FirebaseImage from '../Media/FirebaseImage';
import { uploadCompanyLogo } from '../../Utils/uploads';
import { FieldGrid, Full, SectionCard, SectionHeader } from './ProfilePageShell';
import { Label, PasswordHint, Select, SelectGroup, TextArea } from '../../Containers/Auth/Profile/ProfileFormLayout';

const TAX_OPTIONS = [
  { value: '', label: 'À choisir pour chaque devis' },
  { value: 'exempt', label: 'TVA non applicable - art. 293 B du CGI' },
  { value: '5.5', label: 'TVA 5,5 %' },
  { value: '10', label: 'TVA 10 %' },
  { value: '20', label: 'TVA 20 %' },
];

const ArtisanDocumentTemplateCard = ({ values, handleChange, setFieldValue }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');

  const handleLogo = async (file) => {
    if (!file || uploading) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setUploadError('Pour les PDF, utilisez un logo PNG ou JPG.');
      return;
    }
    setUploadError('');
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadCompanyLogo(file, setProgress);
      setFieldValue('companyLogo', url, true);
    } catch (error) {
      setUploadError(error?.message || 'Le logo n’a pas pu être envoyé.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <div>
          <h2>Mon modèle de devis et contrat</h2>
          <p>Renseignez ces informations une seule fois. Elles prérempliront ensuite chaque devis.</p>
        </div>
      </SectionHeader>

      <TemplateIntro>
        <DescriptionOutlinedIcon />
        <div>
          <strong>Gagnez du temps après vos chantiers</strong>
          <span>Le logo Le Petit Boulot reste discret comme preuve de la plateforme, tandis que votre entreprise devient l’identité principale du document.</span>
        </div>
      </TemplateIntro>

      <TemplateLayout>
        <LogoPanel>
          <LogoPreview>
            {values.companyLogo ? (
              <FirebaseImage src={values.companyLogo} alt={`Logo de ${values.businessName || 'l’entreprise'}`} />
            ) : (
              <LogoFallback><BusinessOutlinedIcon /><span>Votre logo</span></LogoFallback>
            )}
          </LogoPreview>
          <LogoActions>
            <UploadButton type="button" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <CloudUploadOutlinedIcon />
              {uploading ? `Envoi ${progress}%` : values.companyLogo ? 'Changer le logo' : 'Ajouter mon logo'}
            </UploadButton>
            {values.companyLogo && (
              <RemoveButton type="button" onClick={() => setFieldValue('companyLogo', '', true)}>
                <DeleteOutlineIcon /> Retirer
              </RemoveButton>
            )}
          </LogoActions>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            hidden
            onChange={(event) => {
              handleLogo(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <PasswordHint>Logo carré ou horizontal, PNG ou JPG, 8 Mo maximum.</PasswordHint>
          {uploadError && <UploadError>{uploadError}</UploadError>}
        </LogoPanel>

        <DocumentPreview aria-label="Aperçu du modèle de document">
          <PreviewHeader>
            <div>
              {values.companyLogo ? <FirebaseImage src={values.companyLogo} alt="" /> : <PreviewMark>PB</PreviewMark>}
              <span>{values.businessName || 'Votre entreprise'}</span>
            </div>
            <small>DEVIS / CONTRAT</small>
          </PreviewHeader>
          <PreviewLine />
          <PreviewText>{values.legalForm || 'Forme juridique'} · {values.siret ? `SIRET ${values.siret}` : 'SIRET à compléter'}</PreviewText>
          <PreviewBox>Client, chantier, planning et prix seront remplis automatiquement.</PreviewBox>
          <PreviewFooter>{values.documentFooter || 'Merci pour votre confiance.'}</PreviewFooter>
          <PlatformSeal>Document suivi via Le Petit Boulot</PlatformSeal>
        </DocumentPreview>
      </TemplateLayout>

      <FieldGrid>
        <SelectGroup>
          <Label htmlFor="businessName">Nom commercial / entreprise</Label>
          <Input id="businessName" name="businessName" value={values.businessName} onChange={handleChange} placeholder="Ex. Dupont Menuiserie" />
        </SelectGroup>
        <SelectGroup>
          <Label htmlFor="legalForm">Forme juridique</Label>
          <Input id="legalForm" name="legalForm" value={values.legalForm} onChange={handleChange} placeholder="Ex. Micro-entrepreneur, SASU, SARL" />
        </SelectGroup>
        <SelectGroup>
          <Label htmlFor="siret">SIRET</Label>
          <Input id="siret" name="siret" value={values.siret} onChange={handleChange} inputMode="numeric" placeholder="14 chiffres" />
        </SelectGroup>
        <SelectGroup>
          <Label htmlFor="vatNumber">N° de TVA intracommunautaire</Label>
          <Input id="vatNumber" name="vatNumber" value={values.vatNumber} onChange={handleChange} placeholder="Facultatif selon votre régime" />
        </SelectGroup>
        <Full>
          <SelectGroup>
            <Label htmlFor="professionalInsurance">Assurance professionnelle</Label>
            <Input id="professionalInsurance" name="professionalInsurance" value={values.professionalInsurance} onChange={handleChange} placeholder="Assureur, numéro de police, garantie décennale si applicable" />
          </SelectGroup>
        </Full>
        <SelectGroup>
          <Label htmlFor="companyWebsite">Site web</Label>
          <Input id="companyWebsite" name="companyWebsite" value={values.companyWebsite} onChange={handleChange} inputMode="url" placeholder="https://..." />
        </SelectGroup>
        <SelectGroup>
          <Label htmlFor="quoteValidityDays">Validité habituelle des devis</Label>
          <Input id="quoteValidityDays" name="quoteValidityDays" type="number" min="1" max="365" value={values.quoteValidityDays} onChange={handleChange} />
        </SelectGroup>
        <SelectGroup>
          <Label htmlFor="defaultTaxMode">Régime de TVA par défaut</Label>
          <Select id="defaultTaxMode" name="defaultTaxMode" value={values.defaultTaxMode} onChange={handleChange}>
            {TAX_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </SelectGroup>
        <Full>
          <SelectGroup>
            <Label htmlFor="paymentTerms">Conditions de paiement habituelles</Label>
            <TextArea id="paymentTerms" name="paymentTerms" value={values.paymentTerms} onChange={handleChange} maxLength={500} placeholder="Ex. Acompte de 20 %, solde après réception, pénalités éventuelles..." />
            <PasswordHint>{values.paymentTerms.length}/500 caractères</PasswordHint>
          </SelectGroup>
        </Full>
        <Full>
          <SelectGroup>
            <Label htmlFor="documentFooter">Message de bas de page</Label>
            <TextArea id="documentFooter" name="documentFooter" value={values.documentFooter} onChange={handleChange} maxLength={240} placeholder="Ex. Merci pour votre confiance. Intervention réalisée avec soin et transparence." />
            <PasswordHint>{values.documentFooter.length}/240 caractères</PasswordHint>
          </SelectGroup>
        </Full>
      </FieldGrid>
    </SectionCard>
  );
};

const TemplateIntro = styled.div`
  display:flex;align-items:flex-start;gap:.75rem;margin-bottom:1rem;padding:.85rem 1rem;border-radius:.9rem;background:#eef5f8;color:#254c65;
  svg{flex:0 0 auto;margin-top:.1rem}div{display:grid;gap:.2rem}strong{font-size:.86rem}span{font-size:.76rem;line-height:1.5}
`;
const TemplateLayout = styled.div`display:grid;grid-template-columns:minmax(12rem,.8fr) minmax(0,1.2fr);gap:1rem;margin-bottom:1rem;@media(max-width:700px){grid-template-columns:1fr;}`;
const LogoPanel = styled.div`display:grid;align-content:start;gap:.65rem;padding:1rem;border:1px solid #e4ddd2;border-radius:1rem;background:#faf8f4;`;
const LogoPreview = styled.div`
  min-height:8rem;display:grid;place-items:center;overflow:hidden;border:1px dashed #cfc4b4;border-radius:.9rem;background:#fff;
  img{width:100%;height:8rem;object-fit:contain;padding:.75rem;box-sizing:border-box}
`;
const LogoFallback = styled.div`display:grid;place-items:center;gap:.35rem;color:#806c50;svg{font-size:2rem}span{font-size:.76rem;font-weight:800}`;
const LogoActions = styled.div`display:flex;flex-wrap:wrap;gap:.5rem;`;
const UploadButton = styled.button`flex:1;min-height:2.7rem;display:inline-flex;align-items:center;justify-content:center;gap:.45rem;padding:.6rem .75rem;border:1px solid #111;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.76rem;font-weight:850;cursor:pointer;&:disabled{opacity:.55;cursor:wait}`;
const RemoveButton = styled.button`min-height:2.7rem;display:inline-flex;align-items:center;justify-content:center;gap:.35rem;padding:.6rem .75rem;border:1px solid #ead2cf;border-radius:999px;background:#fff4f2;color:#8b302a;font:inherit;font-size:.75rem;font-weight:800;cursor:pointer;`;
const UploadError = styled.p`margin:0;color:#a52e27;font-size:.74rem;line-height:1.4;`;
const DocumentPreview = styled.div`min-width:0;padding:1rem;border:1px solid #ddd6ca;border-radius:1rem;background:#fff;box-shadow:0 .7rem 2rem rgba(17,17,17,.08);`;
const PreviewHeader = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.75rem;min-width:0;div{min-width:0;display:flex;align-items:center;gap:.6rem;font-weight:950;overflow-wrap:anywhere}img{width:3rem;height:2.2rem;object-fit:contain}small{flex:0 0 auto;color:#17344b;font-weight:900;letter-spacing:.08em}`;
const PreviewMark = styled.span`width:2.4rem;height:2.4rem;display:grid;place-items:center;border-radius:.65rem;background:#111;color:#c0a375;font-weight:950;`;
const PreviewLine = styled.div`height:2px;margin:.8rem 0;background:#c0a375;`;
const PreviewText = styled.div`color:#5f6b75;font-size:.72rem;line-height:1.5;overflow-wrap:anywhere;`;
const PreviewBox = styled.div`margin-top:.8rem;padding:.75rem;border-radius:.7rem;background:#f4f6f7;color:#304a5c;font-size:.75rem;line-height:1.45;`;
const PreviewFooter = styled.div`margin-top:.8rem;color:#5f6b75;font-size:.7rem;font-style:italic;line-height:1.45;overflow-wrap:anywhere;`;
const PlatformSeal = styled.div`margin-top:.8rem;padding-top:.6rem;border-top:1px solid #e5e0d8;color:#8b6b38;font-size:.64rem;font-weight:850;text-align:right;`;
const Input = styled.input`width:100%;min-width:0;min-height:3rem;box-sizing:border-box;padding:.72rem .82rem;border:1px solid #d8d0c5;border-radius:.75rem;background:#fff;color:#111;font:inherit;font-size:16px;&:focus{outline:3px solid rgba(192,163,117,.22);border-color:#c0a375}`;

export default ArtisanDocumentTemplateCard;
